import React from 'react';
import { nlBr } from '@sizeid/commons/utils/strings';
import Button from '@sizeid/commons/components/Button';
import { BUSINESS_ORGANIZATIONS } from '@sizeid/shared/constants/routes';

import { BUSINESS_THEME } from 'constants/index';
import app from 'components/app';
import Page from 'components/Page';
import Section from 'components/Page/Section';
import Wrapper from 'components/Page/Wrapper';
import H1 from 'components/typo/Heading/H1';
import H2 from 'components/typo/Heading/H2';
import P from 'components/typo/P';
import Features from 'components/Features';
import {
  WHERE_TO_USE,
  EXPERIENCE,
  REFERENCE,
  PRICING,
  ADVISOR,
  COMPLEX_TECHNOLOGIES,
  STATISTICS,
  PLATFORMS,
} from 'components/Navigation/constants';
import { useTranslation } from 'lib/i18n';

import { jumbotron, features, advisor } from '../home/styles';

import AdvisorImage from './assets/advisor.png';
import AdvisorImage2x from './assets/advisor-2x.png';
import ComplexTechnologiesImage from './assets/complexTechnologies.png';
import ComplexTechnologiesImage2x from './assets/complexTechnologies-2x.png';
import StatisticsImage from './assets/statistics.png';
import StatisticsImage2x from './assets/statistics-2x.png';
import BusinessTopButtons from './BusinessTopButtons';
import BusinessTopImage from './BusinessTopImage';
import BusinessBenefits from './BusinessBenefits';
import BusinessCaseStudy from './BusinessCaseStudy';
import BusinessCarousel from './BusinessCarousel';
import Pricing from './Pricing';
import AnnaFlipchart from './images/Anna_Flipchart.svg';
import { BUSINESS_SIGN_IN, BUSINESS_SIGN_UP } from './constants';
import BusinessIntegration from './BusinessIntegration';
import RowImageTitleText from './components/RowImageTitleText';

const BusinessIndex: React.FC = () => {
  const { t, i18n } = useTranslation('pages');

  const lang = i18n.language;
  return (
    <Page title={t('business.index.title')} footer noPadding scrollingHeader>
      <>
        <Section css={jumbotron} beforeWave headerColor>
          <Wrapper>
            <>
              <H1>{t('business.index.h1')}</H1>
              <P>{t('business.index.description')}</P>
              <BusinessTopButtons>
                <Button link={BUSINESS_SIGN_UP(lang)} giant>
                  {t('business.index.createAccount')}
                </Button>
                <Button link={BUSINESS_SIGN_IN(lang)} giant outline white>
                  {t('business.index.login')}
                </Button>
              </BusinessTopButtons>
              <BusinessTopImage src={AnnaFlipchart} />
            </>
          </Wrapper>
        </Section>
        <Section id={ADVISOR} css={advisor} topWave white>
          <Wrapper>
            <RowImageTitleText
              title={t('business.index.advisor.title')}
              text={t('business.index.advisor.text')}
              src={AdvisorImage}
              alt="An image of sizeid advisor"
              srcSet={{ '1x': AdvisorImage, '2x': AdvisorImage2x }}
            />
          </Wrapper>
        </Section>
        <Section id={COMPLEX_TECHNOLOGIES} white css="padding-bottom: 0;">
          <Wrapper>
            <RowImageTitleText
              title={t('business.index.complexTechnologies.title')}
              text={t('business.index.complexTechnologies.text')}
              src={ComplexTechnologiesImage}
              alt="An image of recommendation with advisor"
              srcSet={{
                '1x': ComplexTechnologiesImage,
                '2x': ComplexTechnologiesImage2x,
              }}
              reverse
            />
          </Wrapper>
        </Section>
        <Section id={STATISTICS} white css="padding-bottom: 0 !important;">
          <Wrapper>
            <RowImageTitleText
              title={t('business.index.statistics.title')}
              text={t('business.index.statistics.text')}
              src={StatisticsImage}
              alt="An image of a graph with returns"
              srcSet={{ '1x': StatisticsImage, '2x': StatisticsImage2x }}
            />
          </Wrapper>
        </Section>
        <Section css={features} white>
          <Wrapper css="padding-bottom: 0 !important;">
            <>
              <BusinessBenefits />
              <H2 id={WHERE_TO_USE}>{t('business.index.usage.title')}</H2>
              <P bigOnDesktop>{nlBr(t('business.index.usage.text'))}</P>
              <Features
                base="business.index.usage.features"
                keys={['shop', 'organizations', 'company']}
                imageSize={80}
              />
              {['cs', 'en'].includes(i18n.language) && (
                <Button
                  link={BUSINESS_ORGANIZATIONS.route}
                  outline
                  css="margin-top: 2em;"
                >
                  {t('business.organizations.index.more')}
                </Button>
              )}
            </>
          </Wrapper>
        </Section>
        <Section id={EXPERIENCE} beforeWave gray topWave flipTopWave>
          <Wrapper>
            <BusinessCaseStudy />
          </Wrapper>
        </Section>
        <Section id={REFERENCE} white topWave>
          <Wrapper>
            <>
              <H2>{t('business.index.about.title')}</H2>
              <P bigOnDesktop lead>
                {nlBr(t('business.index.about.text'))}
              </P>
              <BusinessCarousel />
            </>
          </Wrapper>
        </Section>
        <Section id={PLATFORMS} white beforeWave>
          <Wrapper>
            <>
              <H2>{t('business.index.platforms.title')}</H2>
              <P bigOnDesktop lead>
                {nlBr(t('business.index.platforms.text'))}
              </P>
              <BusinessIntegration />
            </>
          </Wrapper>
        </Section>
        <Section id={PRICING} gray topWave>
          <Wrapper>
            <>
              <H2>{t('business.index.pricing.title')}</H2>
              <P bigOnDesktop dark>
                {nlBr(t('business.index.pricing.text'))}
              </P>
              <Pricing />
            </>
          </Wrapper>
        </Section>
      </>
    </Page>
  );
};

export default app(BusinessIndex, {
  theme: BUSINESS_THEME,
});
